import React, { useState, useEffect } from "react";
import { parse, format, isValid } from "date-fns";
import dateOfBirthAndCityValidationSchema from "../../utils/validation/dateOfBirthAndCityValidationSchema";
import { DateOfBirthAndCityType } from "../../static/types/commonTypes/dateOfBirthAndCity";
import FieldDescription from "../tools/FieldDescription";
import PostalCodeField from "./PostalCodeField";
import "../../styles/dateBirth.css";
import useValidation from "../../hooks/useValidation";

type DateOfBirthAndCityProps = {
  checked: boolean;
  dateOfBirth: DateOfBirthAndCityType;
  onChange: (data: Partial<DateOfBirthAndCityType>) => void;
  onValidationChange: (isValid: boolean) => void;
  index?: number
};

const DateOfBirthAndCityComponent: React.FC<DateOfBirthAndCityProps> = ({
  checked,
  dateOfBirth,
  onChange,
  onValidationChange,
  index = 0
}) => {

  const { errors, isValid: dataValidity } = useValidation( dateOfBirth, dateOfBirthAndCityValidationSchema )
  const [ selectedDate, setSelectedDate ] = useState<string>("");

  useEffect(() => {
    onValidationChange( dataValidity )
  }, [ dataValidity, onValidationChange ])


  useEffect(() => {
    if (dateOfBirth.dateOfBirth) {
      const parsed = parse(dateOfBirth.dateOfBirth, "dd/MM/yyyy", new Date());
      if (isValid(parsed)) {
        setSelectedDate(format(parsed, "yyyy-MM-dd"));
      }
    }
  }, [dateOfBirth]);

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    if (!inputValue) {
      setSelectedDate("");
      onChange({ dateOfBirth: "" });
      return;
    }
    const parsed = parse(inputValue, "yyyy-MM-dd", new Date());
    if (!isValid(parsed)) {
      setSelectedDate("");
      onChange({ dateOfBirth: "" });
      return;
    }
    const formattedDate = format(parsed, "dd/MM/yyyy");
    setSelectedDate(inputValue);
    onChange({ dateOfBirth: formattedDate });
  };

  const openDatePicker = (id: string) => {
    const input = document.getElementById(id) as HTMLInputElement;
    input?.showPicker?.();
  };

  return (
    <div className="container my-4">
      <div className="card-body">
        <div className="mb-3 row">
          <label htmlFor="date" className="col-sm-3 col-form-label">
            Date de naissance <strong className="text-danger">*</strong>
          </label>
          <div className="col-sm-6">
            <input
              className={`description-input form-control ${
                checked && errors.dateOfBirth ? "is-invalid" : ""
              }`}
              type="date"
              id={`date${ index }`}
              value={selectedDate}
              onChange={handleDateChange}
              onClick={() => openDatePicker(`date${ index }`)}
              placeholder="JJ/MM/AAAA"
            />
            {
              checked &&
              errors.dateOfBirth &&
              <div className="invalid-feedback"> { errors.dateOfBirth } </div>
            }
            <FieldDescription description="Format : JJ/MM/AAAA" />
          </div>
        </div>
        <div className="row">
          <PostalCodeField
            checked={checked}
            errors={errors}
            postalCode={dateOfBirth.postalCode}
            onChange = {( partialData: Partial<DateOfBirthAndCityType> ) => onChange({ ...dateOfBirth, ...partialData })}
          />
        </div>
        <div className="row mb-3">
          <label htmlFor="city" className="col-sm-2 col-form-label">
            Ville <strong className="text-danger">*</strong>
          </label>
          <div className="col-sm-4">
            <select
              id="city"
              className={`form-select ${ checked && errors.city ? "is-invalid" : ""}`}
              value = { dateOfBirth.city }
              onChange = { event => onChange({ city: event.target.value })}
            >
              {
                dateOfBirth.cities?.map((city, idx) => (
                  <option key={idx} value={city}>
                    {city}
                  </option>
                ))
              }
            </select>
            {
              checked &&
              errors.city &&
              <div className="invalid-feedback"> { errors.city } </div>
            }
            <FieldDescription description="Sélectionnez la ville associée à votre code postal" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DateOfBirthAndCityComponent;
